import { useQuery, useMutation } from "@tanstack/react-query";
import type {
  GetNewsRequest,
  New,
  CreateMaintenanceNewRequest,
  FirstAidStationNewCreate,
  CreatePoolRegulationNewRequest,
  CreateAdverseEventNewRequest,
  NewReportDataItem,
  GetNewsReportPreviewRequest,
  GetBuildingsWithNewsRequest,
  GetNewsReportDataRequest,
  UpdateNewRequest,
} from "../../../api/client/types/new.types";
import type {
  PaginatedResponse,
  ApiError,
  MessageResponse,
  BuildingOptionsResponse,
  ReportDataResponse,
} from "../../../api/client/types/common.types";
import { newEndpoints } from "../../../api/endpoints/new";
import { DEFAULT_PAGE_SIZE } from "../../../shared/constants/pagination";

/**
 * Builds the default list params (page 1) for the current user.
 * Use when updating list cache after create so the query key matches
 * what useNewsResource uses for the first page.
 */
export function getDefaultNewsListParams(params: {
  building_id?: string;
  lifeguard_id?: string;
}): GetNewsRequest {
  return {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    building_id: params.building_id ?? undefined,
    lifeguard_id: params.lifeguard_id ?? undefined,
  };
}

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
export const newQueryKeys = {
  root: ["new"] as const,
  list: (params?: GetNewsRequest) =>
    [...newQueryKeys.root, "list", params] as const,
  detail: (id: string) => [...newQueryKeys.root, "detail", id] as const,
  create: () => [...newQueryKeys.root, "create"] as const,
  createMaintenance: () => [...newQueryKeys.root, "createMaintenance"] as const,
  createFirstAidStation: () =>
    [...newQueryKeys.root, "createFirstAidStation"] as const,
  createPoolRegulationNew: () =>
    [...newQueryKeys.root, "createPoolRegulationNew"] as const,
  createAdverseEventNew: () =>
    [...newQueryKeys.root, "createAdverseEventNew"] as const,
  reportPreview: (params?: GetNewsReportPreviewRequest) =>
    [...newQueryKeys.root, "reportPreview", params] as const,
  buildingsWithNews: () => [...newQueryKeys.root, "buildingsWithNews"] as const,
  reportData: () => [...newQueryKeys.root, "reportData"] as const,
  update: () => [...newQueryKeys.root, "update"] as const,
  delete: () => [...newQueryKeys.root, "delete"] as const,
};

/**
 * Hook: useGetNews
 * Fetches a paginated list of new with optional filters.
 */
export const useGetNews = (params?: GetNewsRequest) => {
  return useQuery<PaginatedResponse<New>, ApiError>({
    queryKey: newQueryKeys.list(params),
    queryFn: () => newEndpoints.getNews(params),
  });
};

/**
 * Hook: useGetNewById
 * Fetches a new item by ID.
 */
export const useGetNewById = (new_id: string) => {
  return useQuery<New, ApiError>({
    queryKey: newQueryKeys.detail(new_id),
    queryFn: () => newEndpoints.getNewById(new_id),
    enabled: !!new_id, // Only fetch if new_id is provided
  });
};

/**
 * Hook: useCreateMaintenanceNew
 * Creates a maintenance new item.
 */
export const useCreateMaintenanceNew = () => {
  return useMutation<New, ApiError, CreateMaintenanceNewRequest>({
    mutationKey: newQueryKeys.createMaintenance(),
    mutationFn: (data) => newEndpoints.createMaintenanceNew(data),
  });
};

/**
 * Hook: useCreateFirstAidStationNew
 * Creates a first aid station new item.
 */
export const useCreateFirstAidStationNew = () => {
  return useMutation<New, ApiError, FirstAidStationNewCreate>({
    mutationKey: newQueryKeys.createFirstAidStation(),
    mutationFn: (data) => newEndpoints.createFirstAidStationNew(data),
  });
};

/**
 * Hook: useCreatePoolRegulationNew
 * Creates a pool regulation violation news item.
 */
export const useCreatePoolRegulationNew = () => {
  return useMutation<New, ApiError, CreatePoolRegulationNewRequest>({
    mutationKey: newQueryKeys.createPoolRegulationNew(),
    mutationFn: (data) => newEndpoints.createPoolRegulationNew(data),
  });
};

/**
 * Hook: useCreateAdverseEventNew
 * Creates an adverse event news item.
 */
export const useCreateAdverseEventNew = () => {
  return useMutation<New, ApiError, CreateAdverseEventNewRequest>({
    mutationKey: newQueryKeys.createAdverseEventNew(),
    mutationFn: (data) => newEndpoints.createAdverseEventNew(data),
  });
};

/**
 * Hook: useGetNewsReportPreview
 * Fetches a paginated list of news report data preview with optional filters.
 */
export const useGetNewsReportPreview = (params?: GetNewsReportPreviewRequest) => {
  return useQuery<PaginatedResponse<NewReportDataItem>, ApiError>({
    queryKey: newQueryKeys.reportPreview(params),
    queryFn: () => newEndpoints.getNewsReportPreview(params),
  });
};

/**
 * Hook: useGetBuildingsWithNews
 * Fetches distinct buildings that have news in a specific date range.
 * Uses mutation to avoid caching (always fetch fresh data for the date range).
 */
export const useGetBuildingsWithNews = () => {
  return useMutation<
    BuildingOptionsResponse,
    ApiError,
    GetBuildingsWithNewsRequest
  >({
    mutationKey: newQueryKeys.buildingsWithNews(),
    mutationFn: (params) => newEndpoints.getBuildingsWithNews(params),
  });
};

/**
 * Hook: useGetNewsReportData
 * Fetches all news report data for a date range without pagination.
 *
 * Uses mutation to prevent caching — always fetches fresh data on demand,
 * which is important for on-the-fly report/PDF generation.
 *
 * @example
 * ```tsx
 * const reportData = useGetNewsReportData();
 *
 * reportData.mutate(
 *   { start_date: "01/01/2024", end_date: "31/01/2024" },
 *   { onSuccess: (data) => exportTableToPdf({ rows: data.items, ... }) }
 * );
 * ```
 */
export const useGetNewsReportData = () => {
  return useMutation<
    ReportDataResponse<NewReportDataItem>,
    ApiError,
    GetNewsReportDataRequest
  >({
    mutationKey: newQueryKeys.reportData(),
    mutationFn: (params) => newEndpoints.getNewsReportData(params),
  });
};

/**
 * Hook: useUpdateNew
 * Updates an existing news item (partial update).
 * lifeguard_id and building_id are never changed; swimmer is preserved per backend rules.
 */
export const useUpdateNew = () => {
  return useMutation<
    New,
    ApiError,
    { news_id: string; data: UpdateNewRequest }
  >({
    mutationKey: newQueryKeys.update(),
    mutationFn: ({ news_id, data }) =>
      newEndpoints.updateNew(news_id, data),
  });
};

/**
 * Hook: useDeleteNew
 * Deletes a news item by ID.
 */
export const useDeleteNew = () => {
  return useMutation<MessageResponse, ApiError, string>({
    mutationKey: newQueryKeys.delete(),
    mutationFn: (new_id) => newEndpoints.deleteNew(new_id),
  });
};